import type { APIOrganization } from '@visorpro/client'
import { makeAutoObservable, runInAction } from 'mobx'
import { toast } from 'react-toastify'

import { visorPRORestClient } from '../util/api'
import { useEffect } from 'react'
import { observed } from '../util/observed-decorator'
import { DictionaryArray } from '../types'
import { QueueTasksStore } from './queue-tasks-store'

export interface APICreateOrganizationRequest {
    name: string
}

export class OrganizationStore {
    public organizationsById: Record<string, APIOrganization> = {}
    public organizationIds: string[] = []
    public membersBeingAddedByOrganizationId: Record<string, Set<string>> = {}
    public membersBeingRemovedByOrganizationId: Record<string, Set<string>> = {}
    public organizationQueueTasksStoresById: Record<string, QueueTasksStore> =
        {}

    public isFetching = false
    public isUpdating = false

    constructor() {
        makeAutoObservable(this)
    }

    @observed('isFetching')
    public async list() {
        const response = await visorPRORestClient.organization.list()
        const organizations = response.items

        const organizationsById: Record<string, APIOrganization> = {}
        const organizationIds: string[] = []
        const organizationQueueTasksStoresById: Record<
            string,
            QueueTasksStore
        > = {}

        organizations.forEach((organization) => {
            organizationsById[organization.id] = organization
            organizationIds.push(organization.id)
            organizationQueueTasksStoresById[organization.id] =
                new QueueTasksStore()
        })

        runInAction(() => {
            this.organizationsById = organizationsById
            this.organizationIds = organizationIds
            this.organizationQueueTasksStoresById =
                organizationQueueTasksStoresById
        })
    }

    @observed('isUpdating')
    public async create({ name }: APICreateOrganizationRequest) {
        const organization = await visorPRORestClient.organization.create(name)

        runInAction(() => {
            this.organizationsById[organization.id] = organization
            this.organizationIds = [organization.id, ...this.organizationIds]
        })
    }

    @observed('isUpdating')
    public async setCnhPartsRegion(organizationId: string, region: string) {
        try {
            await visorPRORestClient.organization.setCnhPartsRegion(
                organizationId,
                region,
            )
            runInAction(() => {
                this.organizationsById[organizationId].cnh_parts_region = region
            })
        } catch (e) {
            toast.error(
                `Failed to set CNH parts region: ${(e as Error).message}`,
            )
        }
    }

    @observed('isUpdating')
    public async setFeatureFlag(
        organizationId: string,
        flagName: string,
        enabled: boolean,
    ) {
        try {
            await visorPRORestClient.organization.setFeatureFlag(
                organizationId,
                flagName,
                enabled,
            )

            runInAction(() => {
                const organization = this.organizationsById[organizationId]
                if (!organization) return

                this.organizationsById = {
                    ...this.organizationsById,
                    [organizationId]: {
                        ...organization,
                        feature_flags: {
                            ...organization.feature_flags,
                            [flagName]: enabled,
                        },
                    },
                }
            })
        } catch (e) {
            toast.error(`Failed to set ${flagName}: ${(e as Error).message}`)
        }
    }

    public async uploadLogo(organizationId: string, file: File) {
        const logo = await visorPRORestClient.organization.uploadLogo(
            organizationId,
            file,
        )

        runInAction(() => {
            if (this.organizationsById[organizationId]) {
                this.organizationsById = {
                    ...this.organizationsById,
                    [organizationId]: {
                        ...this.organizationsById[organizationId],
                        logo: logo,
                    },
                }
            }
        })
    }

    public async addOrganizationMember(organizationId: string, userId: string) {
        if (this.membersBeingAddedByOrganizationId[organizationId]?.has(userId))
            return

        if (!this.membersBeingAddedByOrganizationId[organizationId]) {
            this.membersBeingAddedByOrganizationId[organizationId] = new Set()
        }

        this.membersBeingAddedByOrganizationId[organizationId].add(userId)

        try {
            await visorPRORestClient.organization.createMembership(
                organizationId,
                userId,
            )
        } finally {
            runInAction(() => {
                const membersBeingAdded =
                    this.membersBeingAddedByOrganizationId[organizationId]

                if (membersBeingAdded) {
                    membersBeingAdded.delete(userId)
                }
            })
        }
    }

    public async removeOrganizationMember(
        organizationId: string,
        userId: string,
    ) {
        if (
            this.membersBeingRemovedByOrganizationId[organizationId]?.has(
                userId,
            )
        )
            return

        if (!this.membersBeingRemovedByOrganizationId[organizationId]) {
            this.membersBeingRemovedByOrganizationId[organizationId] = new Set()
        }

        this.membersBeingRemovedByOrganizationId[organizationId].add(userId)

        try {
            await visorPRORestClient.organization.deleteMembership(
                organizationId,
                userId,
            )
        } finally {
            runInAction(() => {
                const membersBeingRemoved =
                    this.membersBeingRemovedByOrganizationId[organizationId]

                if (membersBeingRemoved) {
                    membersBeingRemoved.delete(userId)
                }
            })
        }
    }
}

export const useOrganizations = (store: OrganizationStore) => {
    useEffect(() => {
        if (!store.organizationIds.length) {
            void store.list()
        }
    }, [store])

    return new DictionaryArray<APIOrganization>(
        Object.values(store.organizationsById),
        'id',
    )
}

export const useOrganization = (
    store: OrganizationStore,
    organizationId: string | undefined,
) => {
    useEffect(() => {
        if (organizationId && !store.organizationsById[organizationId]) {
            void store.list()
        }
    }, [store, organizationId])

    if (organizationId) return store.organizationsById[organizationId]
}
